import React from 'react'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import { cn } from '@/lib/utils/cn'

/* ------------------------------------------------------------------ */
/*  Tooltip — Radix-based with 400ms delay, arrow, keyboard reveal     */
/*  See ARCHITECTURE.md §17                                            */
/* ------------------------------------------------------------------ */

export interface TooltipProps {
  /** Content shown in the tooltip */
  content: React.ReactNode
  /** Side preference */
  side?: 'top' | 'right' | 'bottom' | 'left'
  /** Side offset in px (default 8) */
  sideOffset?: number
  /** Open delay in ms (default 400) */
  delayDuration?: number
  /** The trigger element */
  children: React.ReactNode
}

export function TooltipProvider({ children }: { children: React.ReactNode }) {
  return (
    <TooltipPrimitive.Provider delayDuration={400}>{children}</TooltipPrimitive.Provider>
  )
}

export function Tooltip({
  content,
  side = 'top',
  sideOffset = 8,
  delayDuration,
  children,
}: TooltipProps) {
  return (
    <TooltipPrimitive.Root delayDuration={delayDuration}>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          sideOffset={sideOffset}
          className={cn(
            'z-50 overflow-hidden rounded px-3 py-1.5 text-xs font-medium',
            'bg-neutral-900/90 text-white',
            'animate-in fade-in-0 zoom-in-95',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
          )}
        >
          {content}
          <TooltipPrimitive.Arrow className="fill-neutral-900/90" />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  )
}

Tooltip.displayName = 'Tooltip'
TooltipProvider.displayName = 'TooltipProvider'
